import { Timestamp } from 'firebase/firestore';

/**
 * Status types for suggestions
 * - in_progress: Active suggestion accepting votes (진행중)
 * - closed: Automatically closed after 7 days (종료)
 * - pending: Under review (검토중)
 * - completed: Accepted and completed (완료)
 * - rejected: Rejected by admin (거부)
 */
export type SuggestionStatus = 'in_progress' | 'closed' | 'pending' | 'completed' | 'rejected';

/**
 * Main Suggestion interface
 * Extends the structure from suggesstion.md with admin reply fields
 */
export interface Suggestion {
  id: string;
  userId: string;                    // Author's Firebase UID
  userName: string;                  // Currently empty, not displayed
  userOrganization: string;          // Author's organization
  title: string;                     // Suggestion title
  content: string;                   // Suggestion content
  category: string;                  // Category (e.g., "일반")
  createdAt: Timestamp;              // Creation timestamp
  upvotes: number;                   // Number of upvotes
  downvotes: number;                 // Number of downvotes
  upvotedBy: string[];               // Array of user UIDs who upvoted
  downvotedBy: string[];             // Array of user UIDs who downvoted
  status: SuggestionStatus;          // Current status

  // Admin reply fields (NEW)
  reply?: string;                    // Admin's reply text
  repliedAt?: Timestamp;             // Reply timestamp
  repliedBy?: string;                // Admin UID who replied
}

/**
 * Helper function to convert status to Korean display text
 */
export const getStatusDisplayName = (status: SuggestionStatus | string): string => {
  const statusMap: Record<string, string> = {
    'in_progress': '진행중',
    'closed': '종료',
    'pending': '검토중',
    'completed': '완료',
    'rejected': '거부',
    // Legacy Korean values (for compatibility)
    '진행중': '진행중',
    '종료': '종료',
    '검토중': '검토중',
    '완료': '완료',
    '거부': '거부',
  };
  return statusMap[status] || status;
};

/**
 * Helper function to get status color for chips
 */
export const getStatusColor = (status: SuggestionStatus | string):
  'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case 'in_progress':
    case '진행중':
      return 'primary';
    case 'closed':
    case '종료':
      return 'default';
    case 'pending':
    case '검토중':
      return 'warning';
    case 'completed':
    case '완료':
      return 'success';
    case 'rejected':
    case '거부':
      return 'error';
    default:
      return 'default';
  }
};
